import React, { createContext, useRef } from 'react';
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import Pool from './UserPool';

// Refresh the idToken this many ms before its `exp` so any in-flight or
// imminent API call sees a fresh token instead of racing the expiry.
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const AccountContext = createContext();

const Account = props => {
    const refreshTimerRef = useRef(null);

    const clearRefreshTimer = () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    };

    const scheduleRefresh = session => {
        clearRefreshTimer();
        const expMs = session.idToken.payload.exp * 1000;
        const delay = Math.max(0, expMs - Date.now() - REFRESH_BUFFER_MS);
        refreshTimerRef.current = setTimeout(() => {
            const user = Pool.getCurrentUser();
            if (!user) return;
            user.refreshSession(session.getRefreshToken(), (err, newSession) => {
                if (err) {
                    console.error('Token refresh failed:', err);
                    return;
                }
                scheduleRefresh(newSession);
            });
        }, delay);
    };

    const getSession = async () =>
        await new Promise((resolve, reject) => {
            const user = Pool.getCurrentUser();
            if (user) {
                user.getSession((err, session) => {
                    if (err) {
                        reject();
                    } else {
                        scheduleRefresh(session);
                        resolve(session);
                    }
                });
            } else {
                reject();
            }
        });

    const authenticate = (Username, Password) =>
        new Promise((resolve, reject) => {
            const user = new CognitoUser({ Username, Pool });
            const authDetails = new AuthenticationDetails({ Username, Password });
            user.authenticateUser(authDetails, {
                onSuccess: data => {
                    console.log("Clearing out all other sessions")
                    user.globalSignOut();
                    user.authenticateUser(authDetails, {
                        onSuccess: data => {
                            console.log('Login Success:', data);
                            scheduleRefresh(data);
                            resolve({ data: data, new_password_required: false });
                        }
                    })
                },
                onFailure: err => {
                    console.error('Login Failure:', err);
                    reject(err);
                },
                newPasswordRequired: data => {
                    console.log('New Password Required:', data);
                    resolve({ data: data, new_password_required: true, user_session: user });
                }
            });
        });

    const logout = () => {
        clearRefreshTimer();
        const user = Pool.getCurrentUser();
        if (user) {
            user.signOut();
            console.log("Signed out.");
        }
    }


    return (
        <AccountContext.Provider value={{
            authenticate,
            getSession,
            logout
        }}>
            {props.children}
        </AccountContext.Provider>
    );
};

export { Account, AccountContext };